export type RideStatus =
  | 'requested'
  | 'offering'
  | 'accepted'
  | 'driver_arrived'
  | 'in_progress'
  | 'finished'
  | 'cancelled'
  | 'no_drivers';

export type Actor = 'passenger' | 'driver' | 'system';

/**
 * Máquina de estados da corrida. Fonte única de verdade — a API valida
 * toda transição aqui antes de tocar no banco.
 *
 * Quem pode fazer cada transição faz parte da regra: motorista não
 * inicia corrida que não é dele, passageiro não marca "cheguei".
 */
const TRANSITIONS: Readonly<Record<RideStatus, Readonly<Partial<Record<RideStatus, readonly Actor[]>>>>> = {
  requested: {
    offering: ['system'],
    no_drivers: ['system'],
    cancelled: ['passenger'],
  },
  offering: {
    accepted: ['driver'],
    no_drivers: ['system'],
    cancelled: ['passenger'],
  },
  accepted: {
    driver_arrived: ['driver'],
    cancelled: ['passenger', 'driver'],
  },
  driver_arrived: {
    in_progress: ['driver'],
    cancelled: ['passenger', 'driver'],
  },
  // Corrida iniciada não cancela. Termina.
  in_progress: {
    finished: ['driver'],
  },
  finished: {},
  cancelled: {},
  no_drivers: {},
};

export const ACTIVE_STATUSES: readonly RideStatus[] = [
  'requested',
  'offering',
  'accepted',
  'driver_arrived',
  'in_progress',
];

export function isActive(status: RideStatus): boolean {
  return ACTIVE_STATUSES.includes(status);
}

/**
 * Se a tela de avaliação deve aparecer para quem está olhando.
 *
 * As três condições existem por motivos diferentes, e faltando qualquer
 * uma a tela vira laço: `finished` porque só corrida concluída se avalia,
 * `rated` porque só o servidor sabe se a nota já foi dada — o status
 * continua 'finished' depois dela —, e `skippedId` porque "agora não"
 * precisa valer contra a própria releitura que vem logo em seguida.
 */
export function shouldPromptRating(
  last: { id: number; status: string; rated?: boolean } | undefined | null,
  skippedId: number | null,
): boolean {
  return !!last && last.status === 'finished' && !last.rated && last.id !== skippedId;
}

export function canTransition(from: RideStatus, to: RideStatus, actor: Actor): boolean {
  return TRANSITIONS[from][to]?.includes(actor) ?? false;
}

export class InvalidTransitionError extends Error {
  readonly code = 'INVALID_TRANSITION';
  constructor(from: RideStatus, to: RideStatus, actor: Actor) {
    super(`transição inválida: ${from} -> ${to} por ${actor}`);
  }
}

export function assertTransition(from: RideStatus, to: RideStatus, actor: Actor): void {
  if (!canTransition(from, to, actor)) throw new InvalidTransitionError(from, to, actor);
}
